import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ClockCounterClockwise, ShieldWarning, Trash } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Point 44 — persistent login-audit register (admin + handler, success AND
 * failure) with the 100km-from-previous-login warning, plus point 6's
 * device/IP block-unblock for staff sessions. Deleting audit history is
 * gated behind the admin's own password, per spec.
 */
const LoginAuditAndBlocklist = () => {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [blocked, setBlocked] = useState([]);
  const [showClearModal, setShowClearModal] = useState(false);
  const [clearPassword, setClearPassword] = useState('');
  const [clearBusy, setClearBusy] = useState(false);
  const [blockValue, setBlockValue] = useState('');
  const [blockKind, setBlockKind] = useState('ip');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [auditRes, blockedRes] = await Promise.all([
        axios.get(`${API}/admin/login-audit`, { params: { limit: 100 } }),
        axios.get(`${API}/admin/staff-devices/blocked`),
      ]);
      setRows(auditRes.data?.items || []);
      setBlocked(blockedRes.data?.items || []);
    } catch (e) {
      toast.error('Failed to load login audit');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const clearHistory = async () => {
    if (!clearPassword) { toast.error('Password required'); return; }
    setClearBusy(true);
    try {
      const res = await axios.post(`${API}/admin/login-audit/clear`, {
        admin_id: user.id, password: clearPassword,
      });
      toast.success(`Deleted ${res.data?.deleted || 0} record(s)`);
      setShowClearModal(false);
      setClearPassword('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to clear history');
    } finally {
      setClearBusy(false);
    }
  };

  const blockNow = async () => {
    if (!blockValue.trim()) return;
    try {
      await axios.post(`${API}/admin/staff-devices/block`, {
        admin_id: user.id, kind: blockKind, value: blockValue.trim(),
      });
      toast.success('Blocked');
      setBlockValue('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to block');
    }
  };

  const unblock = async (row) => {
    try {
      await axios.post(`${API}/admin/staff-devices/unblock`, { kind: row.kind, value: row.value });
      toast.success('Unblocked');
      load();
    } catch (e) {
      toast.error('Failed to unblock');
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl shadow p-5" data-testid="login-audit-panel">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <ClockCounterClockwise size={20} weight="fill" className="text-purple-600" /> Login Audit
          </h3>
          <button
            onClick={() => setShowClearModal(true)}
            disabled={rows.length === 0}
            className="text-[11px] font-semibold text-red-600 hover:text-red-800 disabled:text-gray-300 flex items-center gap-1"
            data-testid="login-audit-clear-btn"
          ><Trash size={12} /> Clear history</button>
        </div>
        <div className="max-h-72 overflow-y-auto space-y-1.5">
          {loading ? (
            <div className="text-center py-6 text-sm text-gray-500">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="text-center py-6 text-sm text-gray-500">No login attempts recorded yet.</div>
          ) : rows.map((r) => (
            <div
              key={r.id}
              className={`border rounded-lg px-3 py-2 text-xs ${r.success ? 'border-gray-200' : 'border-red-200 bg-red-50/50'}`}
              data-testid={`login-audit-row-${r.id}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold text-gray-800">{r.username || r.user_id} <span className="text-gray-400 font-normal">({r.role})</span></span>
                <span className={`px-1.5 py-0.5 rounded font-semibold text-[10px] ${r.success ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                  {r.success ? 'success' : `failed — ${r.reason || 'unknown'}`}
                </span>
              </div>
              <div className="text-gray-500 mt-0.5">
                {r.city || r.state ? `${r.city}${r.city && r.state ? ', ' : ''}${r.state} · ` : ''}{r.ip_address} · {new Date(r.created_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
              </div>
              {r.far_from_previous && (
                <div className="mt-1 flex items-center gap-1 text-amber-700 font-semibold">
                  <ShieldWarning size={12} weight="fill" /> {r.distance_km} km from previous login location
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow p-5" data-testid="staff-blocklist-panel">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">Blocked Devices / IPs</h3>
        <div className="flex gap-2 mb-3">
          <select value={blockKind} onChange={(e) => setBlockKind(e.target.value)} className="border border-gray-300 rounded px-2 py-1.5 text-xs">
            <option value="ip">IP</option>
            <option value="fingerprint">Device fingerprint</option>
          </select>
          <input
            value={blockValue}
            onChange={(e) => setBlockValue(e.target.value)}
            placeholder={blockKind === 'ip' ? 'e.g. 203.0.113.5' : 'device fingerprint'}
            className="flex-1 border border-gray-300 rounded px-2 py-1.5 text-xs"
            data-testid="staff-block-value-input"
          />
          <button onClick={blockNow} className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-xs font-semibold rounded" data-testid="staff-block-btn">
            Block
          </button>
        </div>
        <div className="space-y-1.5 max-h-56 overflow-y-auto">
          {blocked.length === 0 ? (
            <div className="text-xs text-gray-400 py-2">Nothing blocked.</div>
          ) : blocked.map((b, i) => (
            <div key={i} className="flex items-center justify-between text-xs border border-gray-100 bg-gray-50 rounded px-2 py-1.5">
              <div>
                <span className="font-mono">{b.value}</span>
                <span className="text-gray-400 ml-1.5">({b.kind})</span>
                {b.reason && <span className="text-gray-500 ml-1.5">— {b.reason}</span>}
              </div>
              <button onClick={() => unblock(b)} className="text-purple-600 hover:text-purple-800 font-semibold" data-testid={`staff-unblock-${i}`}>
                Unblock
              </button>
            </div>
          ))}
        </div>
      </div>

      {showClearModal && (
        <div className="fixed inset-0 bg-black/60 z-[130] flex items-center justify-center p-3" onClick={() => !clearBusy && setShowClearModal(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl w-full max-w-sm p-5 shadow-2xl">
            <h3 className="font-bold text-lg text-slate-800 mb-1">Delete login-audit history</h3>
            <p className="text-[12px] text-slate-600 mb-3">
              Enter your admin password to permanently delete all login-audit records.
            </p>
            <input
              type="password"
              value={clearPassword}
              onChange={(e) => setClearPassword(e.target.value)}
              placeholder="Admin password"
              autoFocus
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm mb-3"
              data-testid="login-audit-clear-password-input"
            />
            <div className="flex gap-2">
              <button onClick={() => setShowClearModal(false)} disabled={clearBusy} className="flex-1 py-2 border border-slate-300 rounded-lg text-sm">Cancel</button>
              <button
                onClick={clearHistory}
                disabled={clearBusy || !clearPassword}
                className="flex-1 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-bold disabled:opacity-50"
                data-testid="login-audit-clear-confirm"
              >{clearBusy ? 'Deleting…' : 'Delete All'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginAuditAndBlocklist;
